/**
 * Shared gate for the structure-management proposals (rename, delete,
 * duplicate, replace contours, type, markers, structure-set label /
 * description / approval, new structure set).
 *
 * These changes have no geometry to preview, so the card states the change
 * in words: a title, a one-line summary, optional details, and whether the
 * viewer can undo it. Apply re-checks, in order: the active structure set
 * is still the one the card was built against; the op's own re-check
 * (e.g. approval, name still free); then runs the op's existing viewer
 * call and returns its outcome (the op confirms the result by reading the
 * value back). Errors become a `failed` outcome, never a throw.
 * Pure/React-free.
 */
import { reasonOf } from './pipeline';

/** Outcomes every gated tool shares (applied payload is per tool). */
type GateOutcome =
    | { readonly status: 'applied' }
    | { readonly status: 'rejected' }
    | { readonly status: 'failed'; readonly reason: string };

export interface ChangeProposal<O> {
    readonly toolCallId: string;
    /** Card title, e.g. "Rename structure". */
    readonly title: string;
    /** The change in one line, e.g. “PTV” → “PTV_boost”. */
    readonly summary: string;
    /** Extra facts the clinician should see before deciding. */
    readonly details: ReadonlyArray<string>;
    /** False when the viewer has no undo for this change. */
    readonly undoable: boolean;
    /** A stronger warning, shown highlighted (e.g. delete, approval). */
    readonly caution?: string;
    apply(): Promise<O>;
    reject(): O;
    /** No geometry overlay for these changes; kept for the shared channel. */
    showPreview(): void;
    hidePreview(): void;
}

export interface ChangeSpec<O> {
    readonly toolCallId: string;
    readonly title: string;
    readonly summary: string;
    readonly details?: ReadonlyArray<string>;
    readonly undoable: boolean;
    readonly caution?: string;
    /** Identity of the active structure set, read live. */
    currentContextId(): string | undefined;
    /** Re-checked at Apply; a string is why the change is now refused. */
    recheck?(): string | undefined;
    /** The existing viewer call + read-back; resolves to the outcome. */
    run(): Promise<O>;
}

export function gatedChange<O extends GateOutcome | { status: string }>(
    spec: ChangeSpec<O>
): ChangeProposal<O> {
    const plannedContextId = spec.currentContextId();
    const failed = (reason: string) => ({ status: 'failed', reason }) as O;
    return {
        toolCallId: spec.toolCallId,
        title: spec.title,
        summary: spec.summary,
        details: spec.details ?? [],
        undoable: spec.undoable,
        ...(spec.caution !== undefined ? { caution: spec.caution } : {}),
        apply: async () => {
            if (spec.currentContextId() !== plannedContextId) {
                return failed(
                    'the active structure set changed before this was ' +
                        'applied; re-issue the request'
                );
            }
            const refused = spec.recheck?.();
            if (refused !== undefined) {
                return failed(refused);
            }
            try {
                return await spec.run();
            } catch (err) {
                return failed(reasonOf(err));
            }
        },
        reject: () => ({ status: 'rejected' }) as O,
        showPreview: () => undefined,
        hidePreview: () => undefined,
    };
}

/** Wait for the viewer's event handlers to settle before reading back. */
export function settle(): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, 0));
}
